import { useInfiniteQuery } from 'react-query'

import { api, useQueryApi } from 'utils/api'

import type { CommonListResponse, CommonResponse } from 'types/api'
import type { UseQueryOptions } from 'react-query'
import type {
  TAPIInspiration,
  TInspirationCategories,
  TInspirationCategory,
} from 'types/inspirations'
import type { Pagination } from 'types/entities'

type TUseExplorationListProps = {
  limit?: number
  lastCursor?: number
  contentSources?: string[]
  categorySlug?: string
}

export function useAPIGetInspirationList({
  limit = 10,
  lastCursor = 0,
  contentSources = [],
  categorySlug,
}: TUseExplorationListProps) {
  // Prepare cache key
  let cacheKey: Array<any> = ['exploration-infinite-query']
  if (contentSources.length >= 1) {
    cacheKey = ['exploration-infinite-query', { categorySlug, contentSources }]
  }

  return useInfiniteQuery<
    CommonListResponse<TAPIInspiration, Pagination>,
    unknown,
    CommonResponse<TAPIInspiration[], Pagination>
  >(
    cacheKey,
    async ({ pageParam = lastCursor }) => {
      return api(
        '/guru/teachers/v1alpha1/inspirations',
        {
          params: {
            limit,
            offset: pageParam,
            contentSources,
            category: categorySlug,
          },
        },
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        true
      )
    },
    {
      getNextPageParam: (
        lastPage: CommonListResponse<TAPIInspiration, Pagination>
      ) => {
        const { limit = 0, offset = 0, total = 0 } = lastPage?.meta ?? {}
        return total > offset + limit ? offset + limit : false
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useAPIGetInspirationLastPublishedChecker(enabled: boolean) {
  return useQueryApi<CommonListResponse<TAPIInspiration, Pagination>>(
    `/guru/teachers/v1alpha1/inspirations`,
    {
      queryConfig: {
        enabled,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      axiosConfig: {
        params: {
          limit: 1,
          offset: 0,
        },
      },
      noAuthorization: true,
    }
  )
}

export function useAPIGetCategories(
  sortBy: 'CT_DEFAULT' | 'CT_ALPHABETICAL' = 'CT_DEFAULT',
  opts?: UseQueryOptions<CommonResponse<TInspirationCategories>>
) {
  return useQueryApi<CommonResponse<TInspirationCategories>>(
    `/guru/guru-komunitas/v1/content/categories?sortBy=${
      sortBy ?? 'CT_DEFAULT'
    }`,
    {
      queryConfig: {
        retry: false,
        useErrorBoundary: false,
        ...opts,
      },
      noAuthorization: true,
    }
  )
}

export function useAPIGetCategory(
  slug: string,
  opts?: UseQueryOptions<CommonResponse<TInspirationCategory>>
) {
  return useQueryApi<CommonResponse<TInspirationCategory>>(
    `/guru/guru-komunitas/v1/content/categories/${slug}`,
    {
      queryConfig: {
        retry: false,
        useErrorBoundary: false,
        ...opts,
      },
      noAuthorization: true,
    }
  )
}
